// ============================================================
// dev-core-web/src/components/Layout.tsx
// Version: 1.1.0
// Purpose:
//   - общий каркас приложения: header + sidebar + main
//   - рендерит children или <Outlet /> (react-router)
//   - навигация — статический список; вызывающий код решает,
//     какие пункты показывать
//   - НЕ делает fetch, НЕ читает RBAC; badge — статический
//     атрибут пункта (NavItem.badge), не данные
//   - НЕ делает security-решений; не логирует значения
//     (SECURITY.md §19.3)
//
// Правила:
//   - только NavLink/Link, не <a href> (SPA-переходы)
//   - aria-current="page" для активного пункта
//   - skip-link для keyboard-навигации (доступность)
//   - sidebar collapsible; состояние — локальное UI-состояние
//   - badge — UI-подсказка, не authority (MONITORING.md §11.1)
//   - никаких console.log
//
// Ссылки:
//   - docs/API.md §21
//   - docs/RBAC.md §15, §16
//   - docs/CLASSIFICATION.md §7.2
//   - docs/SECURITY.md §19.3
//   - docs/IMMERSIVE.md §17
//   - docs/MONITORING.md §11.1
//   - docs/DEVELOPMENT.md §11.2, §11.3
//   - dev-core-web/src/components/EntityCard.tsx
// ============================================================

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  Banknote,
  Boxes,
  Building2,
  ChevronLeft,
  ChevronRight,
  Cog,
  Cpu,
  Database,
  FileKey,
  Gauge,
  GitBranch,
  Glasses,
  Globe,
  HardDrive,
  KeyRound,
  Layers,
  LogOut,
  Menu,
  Moon,
  Network,
  Package,
  Server,
  Shield,
  ShieldCheck,
  Sparkles,
  Sun,
  Tag,
  User,
  Users,
  X,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { ErrorBoundary } from '@/components/ErrorBoundary';

// ------------------------------------------------------------
// Типы
// ------------------------------------------------------------

export type NavBadgeTone = 'default' | 'info' | 'warning' | 'danger';

export interface NavBadge {
  /** Короткий текст: число, или 'new', или '!' */
  label: string;
  tone?: NavBadgeTone;
  /** Опциональный title для tooltip. Не содержит sensitive. */
  title?: string;
}

export interface NavItem {
  /** Путь для NavLink. */
  to: string;
  /** Человекочитаемая метка. */
  label: string;
  /** Иконка (lucide). */
  icon: ReactNode;
  /** Опциональная группа (для группировки в sidebar). */
  group?: string;
  /**
   * Опциональный badge. НЕ security-решение: если не задан —
   * пункт рендерится без badge. Значения badge не должны
   * раскрывать classified данные (RBAC.md §15.5,
   * CLASSIFICATION.md §7.2).
   */
  badge?: NavBadge;
  /** Скрывать ли пункт целиком (например, если нет XR). */
  hidden?: boolean;
}

export interface LayoutProps {
  /** Если задан — рендерится он. Иначе — <Outlet />. */
  children?: ReactNode;
  /**
   * Дополнительные пункты меню, объединяемые с дефолтными.
   * Позволяет вызывающему коду влиять на навигацию.
   */
  extraNav?: NavItem[];
  /** Опциональный breadcrumb (уже готовый JSX). */
  breadcrumb?: ReactNode;
  /** Заголовок приложения / tenant (уже безопасный текст). */
  appTitle?: string;
}

// ------------------------------------------------------------
// Дефолтное меню
// ------------------------------------------------------------
//
// Соответствует API.md §21. Каждый `to` — реальный route.
// Порядок фиксирован; группы — UI-подсказка.

const DEFAULT_NAV: NavItem[] = [
  // Overview
  { to: '/dashboard', label: 'Дашборд', icon: <Activity className="h-4 w-4" aria-hidden />, group: 'Overview' },
  { to: '/metrics', label: 'Метрики', icon: <Activity className="h-4 w-4" aria-hidden />, group: 'Overview' },
  { to: '/logs', label: 'Логи', icon: <FileKey className="h-4 w-4" aria-hidden />, group: 'Overview' },

  // Inventory / Ecosystem
  { to: '/inventory', label: 'Inventory', icon: <Package className="h-4 w-4" aria-hidden />, group: 'Экосистема' },
  { to: '/graph', label: 'Graph', icon: <GitBranch className="h-4 w-4" aria-hidden />, group: 'Экосистема' },
  { to: '/fleet', label: 'Fleet', icon: <Server className="h-4 w-4" aria-hidden />, group: 'Экосистема' },

  // Operations
  { to: '/processes', label: 'Процессы', icon: <Cpu className="h-4 w-4" aria-hidden />, group: 'Операции' },
  { to: '/containers', label: 'Контейнеры', icon: <Boxes className="h-4 w-4" aria-hidden />, group: 'Операции' },
  { to: '/ports', label: 'Порты', icon: <Network className="h-4 w-4" aria-hidden />, group: 'Операции' },
  { to: '/services', label: 'Сервисы', icon: <Cog className="h-4 w-4" aria-hidden />, group: 'Операции' },
  { to: '/sites', label: 'Сайты', icon: <Globe className="h-4 w-4" aria-hidden />, group: 'Операции' },
  { to: '/redis', label: 'Redis', icon: <Database className="h-4 w-4" aria-hidden />, group: 'Операции' },
  { to: '/prometheus', label: 'Prometheus', icon: <Gauge className="h-4 w-4" aria-hidden />, group: 'Операции' },
  { to: '/storage', label: 'Хранилище', icon: <HardDrive className="h-4 w-4" aria-hidden />, group: 'Операции' },
  { to: '/incidents', label: 'Инциденты', icon: <AlertTriangle className="h-4 w-4" aria-hidden />, group: 'Операции' },
  { to: '/access-requests', label: 'Запросы на доступ', icon: <ShieldCheck className="h-4 w-4" aria-hidden />, group: 'Операции' },

  // Identity / RBAC
  { to: '/users', label: 'Пользователи', icon: <Users className="h-4 w-4" aria-hidden />, group: 'Доступ' },
  { to: '/roles', label: 'Роли', icon: <Shield className="h-4 w-4" aria-hidden />, group: 'Доступ' },
  { to: '/permissions', label: 'Permissions', icon: <KeyRound className="h-4 w-4" aria-hidden />, group: 'Доступ' },
  { to: '/clearances', label: 'Clearances', icon: <ShieldCheck className="h-4 w-4" aria-hidden />, group: 'Доступ' },
  { to: '/security', label: 'Безопасность', icon: <ShieldCheck className="h-4 w-4" aria-hidden />, group: 'Доступ' },

  // Tenancy / Revenue
  { to: '/tenants', label: 'Tenants', icon: <Building2 className="h-4 w-4" aria-hidden />, group: 'Коммерция' },
  { to: '/revenue', label: 'Revenue', icon: <Banknote className="h-4 w-4" aria-hidden />, group: 'Коммерция' },
  { to: '/products', label: 'Тарифы', icon: <Tag className="h-4 w-4" aria-hidden />, group: 'Коммерция' },

  // AI / XR
  { to: '/copilot', label: 'Copilot', icon: <Sparkles className="h-4 w-4" aria-hidden />, group: 'AI / XR' },
  { to: '/xr', label: 'XR Console', icon: <Glasses className="h-4 w-4" aria-hidden />, group: 'AI / XR' },

  // Audit
  { to: '/audit', label: 'Audit', icon: <Layers className="h-4 w-4" aria-hidden />, group: 'Аудит' },
];

// ------------------------------------------------------------
// Константы
// ------------------------------------------------------------

const STORAGE_KEY_COLLAPSED = 'dev-core.layout.sidebar.collapsed';
export const STORAGE_KEY_THEME = 'dev-core.layout.theme';

type Theme = 'light' | 'dark';

// ------------------------------------------------------------
// Хелперы
// ------------------------------------------------------------

function badgeToneClass(tone: NavBadgeTone | undefined): string {
  switch (tone) {
    case 'danger':
      return 'bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-200';
    case 'warning':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-200';
    case 'info':
      return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950/60 dark:text-indigo-200';
    case 'default':
    default:
      return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200';
  }
}

function readBoolean(key: string, fallback: boolean): boolean {
  try {
    const v = window.localStorage.getItem(key);
    if (v === '1') return true;
    if (v === '0') return false;
    return fallback;
  } catch {
    return fallback;
  }
}

function writeBoolean(key: string, value: boolean): void {
  try {
    window.localStorage.setItem(key, value ? '1' : '0');
  } catch {
    // localStorage может быть disabled; это не критично.
  }
}

function readTheme(): Theme {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY_THEME);
    if (v === 'dark' || v === 'light') return v;
  } catch {
    // ignore
  }
  // Дефолт — тёмная тема (UI_STYLE §13.1); светлая — только выбором.
  return 'dark';
}

function applyTheme(theme: Theme): void {
  try {
    const root = document.documentElement;
    if (theme === 'dark') root.classList.add('dark');
    else root.classList.remove('dark');
  } catch {
    // SSR / non-DOM окружение
  }
}

// ------------------------------------------------------------
// Группировка меню
// ------------------------------------------------------------

interface NavGroup {
  group: string;
  items: NavItem[];
}

function groupNav(items: NavItem[]): NavGroup[] {
  const order: string[] = [];
  const map = new Map<string, NavItem[]>();
  for (const item of items) {
    const g = item.group ?? 'Прочее';
    if (!map.has(g)) {
      map.set(g, []);
      order.push(g);
    }
    map.get(g)!.push(item);
  }
  return order.map((g) => ({ group: g, items: map.get(g)! }));
}

// ------------------------------------------------------------
// Sidebar
// ------------------------------------------------------------

interface SidebarProps {
  items: NavItem[];
  collapsed: boolean;
  onToggle: () => void;
}

function Sidebar({ items, collapsed, onToggle }: SidebarProps) {
  const groups = useMemo(() => groupNav(items), [items]);

  return (
    <aside
      className={[
        'flex flex-col border-r border-slate-200 bg-white transition-[width]',
        'dark:border-slate-800 dark:bg-slate-900',
        collapsed ? 'w-16' : 'w-64',
      ].join(' ')}
      aria-label="Основная навигация"
    >
      {/* Toggle */}
      <div className="flex items-center justify-end p-2">
        <button
          type="button"
          onClick={onToggle}
          className="inline-flex items-center justify-center rounded-md p-1.5 text-slate-500 hover:bg-muted dark:text-slate-400 dark:hover:bg-muted"
          aria-label={collapsed ? 'Развернуть меню' : 'Свернуть меню'}
          aria-expanded={!collapsed}
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" aria-hidden />
          ) : (
            <ChevronLeft className="h-4 w-4" aria-hidden />
          )}
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-2 pb-4">
        {groups.map((group) => (
          <div key={group.group} className="mb-3">
            {!collapsed && (
              <div className="mb-1 px-2 text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500">
                {group.group}
              </div>
            )}
            <ul className="flex flex-col gap-0.5">
              {group.items.map((item) => {
                const badge = item.badge;
                return (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.to === '/dashboard'}
                      className={({ isActive }) =>
                        [
                          'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors',
                          isActive
                            ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300'
                            : 'text-slate-700 hover:bg-muted dark:text-slate-200 dark:hover:bg-muted',
                        ].join(' ')
                      }
                      title={collapsed ? item.label : undefined}
                    >
                      <span className="shrink-0">{item.icon}</span>
                      {!collapsed && (
                        <>
                          <span className="truncate">{item.label}</span>
                          {badge && (
                            <span
                              className={[
                                'ml-auto inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium',
                                badgeToneClass(badge.tone),
                              ].join(' ')}
                              title={badge.title}
                            >
                              {badge.label}
                            </span>
                          )}
                        </>
                      )}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>
    </aside>
  );
}

// ------------------------------------------------------------
// Header
// ------------------------------------------------------------

interface HeaderProps {
  appTitle: string;
  breadcrumb?: ReactNode;
  theme: Theme;
  onToggleTheme: () => void;
  onToggleMobileSidebar: () => void;
  mobileSidebarOpen: boolean;
}

function Header({
  appTitle,
  breadcrumb,
  theme,
  onToggleTheme,
  onToggleMobileSidebar,
  mobileSidebarOpen,
}: HeaderProps) {
  const { user, logout } = useAuth();
  const { showToast } = useToast();

  const handleLogout = useCallback(async () => {
    try {
      await logout();
      showToast({ variant: 'info', title: 'Сессия завершена' });
    } catch (err) {
      showToast({
        variant: 'error',
        title: 'Не удалось выйти',
        description: err instanceof Error ? err.message : 'Неизвестная ошибка',
      });
    }
  }, [logout, showToast]);

  return (
    <header className="flex items-center gap-3 border-b border-slate-200 bg-white px-3 py-2 dark:border-slate-800 dark:bg-slate-900">
      <button
        type="button"
        onClick={onToggleMobileSidebar}
        className="inline-flex items-center justify-center rounded-md p-1.5 text-slate-500 hover:bg-muted lg:hidden dark:text-slate-400 dark:hover:bg-muted"
        aria-label={mobileSidebarOpen ? 'Закрыть меню' : 'Открыть меню'}
        aria-expanded={mobileSidebarOpen}
      >
        {mobileSidebarOpen ? (
          <X className="h-5 w-5" aria-hidden />
        ) : (
          <Menu className="h-5 w-5" aria-hidden />
        )}
      </button>

      <div className="flex min-w-0 flex-1 items-center gap-3">
        <div className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
          {appTitle}
        </div>
        {breadcrumb && (
          <div className="hidden min-w-0 truncate text-sm text-slate-500 md:block dark:text-slate-400">
            {breadcrumb}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onToggleTheme}
          className="inline-flex items-center justify-center rounded-md p-1.5 text-slate-500 hover:bg-muted dark:text-slate-400 dark:hover:bg-muted"
          aria-label={theme === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
        >
          {theme === 'dark' ? (
            <Sun className="h-4 w-4" aria-hidden />
          ) : (
            <Moon className="h-4 w-4" aria-hidden />
          )}
        </button>

        {user && (
          <div className="hidden items-center gap-2 text-sm md:flex">
            <span className="inline-flex items-center gap-1 text-slate-700 dark:text-slate-300">
              <User className="h-4 w-4" aria-hidden />
              <span className="max-w-[12rem] truncate">
                {user.displayName || user.email || user.id}
              </span>
            </span>
          </div>
        )}

        <button
          type="button"
          onClick={() => void handleLogout()}
          className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-muted dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-muted"
          aria-label="Выйти"
        >
          <LogOut className="h-3.5 w-3.5" aria-hidden />
          <span className="hidden sm:inline">Выйти</span>
        </button>
      </div>
    </header>
  );
}

// ------------------------------------------------------------
// Layout
// ------------------------------------------------------------

export function Layout({
  children,
  extraNav,
  breadcrumb,
  appTitle = 'dev-core',
}: LayoutProps) {
  const location = useLocation();

  const [collapsed, setCollapsed] = useState<boolean>(() =>
    readBoolean(STORAGE_KEY_COLLAPSED, false),
  );
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [theme, setTheme] = useState<Theme>(() => readTheme());

  // Persist collapsed.
  useEffect(() => {
    writeBoolean(STORAGE_KEY_COLLAPSED, collapsed);
  }, [collapsed]);

  // Apply theme на <html>.
  useEffect(() => {
    applyTheme(theme);
    try {
      window.localStorage.setItem(STORAGE_KEY_THEME, theme);
    } catch {
      // ignore
    }
  }, [theme]);

  // Закрыть мобильный sidebar при смене маршрута.
  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [location.pathname]);

  const navItems = useMemo(() => {
    const merged = extraNav ? [...DEFAULT_NAV, ...extraNav] : DEFAULT_NAV;
    return merged.filter((i) => !i.hidden);
  }, [extraNav]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((v) => !v);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme((t) => (t === 'dark' ? 'light' : 'dark'));
  }, []);

  const toggleMobileSidebar = useCallback(() => {
    setMobileSidebarOpen((v) => !v);
  }, []);

  return (
    <ErrorBoundary>
      <div
        data-testid="app-shell"
        className="flex min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100"
      >
        {/* Skip link — accessibility */}
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-white focus:px-3 focus:py-1.5 focus:text-sm focus:text-slate-900 focus:shadow-md dark:focus:bg-slate-900 dark:focus:text-slate-100"
        >
          Перейти к содержимому
        </a>

        {/* Desktop sidebar */}
        <div className="hidden lg:flex">
          <Sidebar
            items={navItems}
            collapsed={collapsed}
            onToggle={toggleCollapsed}
          />
        </div>

        {/* Mobile sidebar overlay */}
        {mobileSidebarOpen && (
          <div className="fixed inset-0 z-40 flex lg:hidden">
            <div
              className="absolute inset-0 bg-black/40"
              role="presentation"
              onClick={() => setMobileSidebarOpen(false)}
            />
            <div className="relative z-10 flex h-full">
              <Sidebar
                items={navItems}
                collapsed={false}
                onToggle={() => setMobileSidebarOpen(false)}
              />
            </div>
          </div>
        )}

        {/* Main column */}
        <div className="flex min-w-0 flex-1 flex-col">
          <Header
            appTitle={appTitle}
            breadcrumb={breadcrumb}
            theme={theme}
            onToggleTheme={toggleTheme}
            onToggleMobileSidebar={toggleMobileSidebar}
            mobileSidebarOpen={mobileSidebarOpen}
          />

          <main
            id="main-content"
            className="min-h-0 flex-1 overflow-y-auto p-4 lg:p-6"
          >
            {children ?? <Outlet />}
          </main>
        </div>
      </div>
    </ErrorBoundary>
  );
}

// ------------------------------------------------------------
// Экспорт
// ------------------------------------------------------------

export default Layout;